import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormControl } from '@angular/forms';
import { Datastore } from '@freelancer/datastore';
import { User, UsersCollection } from '@freelancer/datastore/collections';
import { TimeUtils } from '@freelancer/time-utils';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { Margin } from '@freelancer/ui/margin';
import { SpinnerSize } from '@freelancer/ui/spinner';
import { FontColor, FontWeight } from '@freelancer/ui/text';
import {
  dirtyAndValidate,
  notIncludes,
  required,
} from '@freelancer/ui/validators';
import * as Rx from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

export const DEBOUNCE_TIME = 500;

@Component({
  selector: 'app-payment-share-add-member-input',
  template: `
    <fl-bit
      class="ContentContainer"
      flTrackingSection="PaymentShareAddMemeberInput"
    >
      <fl-bit
        class="ContentContainer ContentContainer-row-pending"
        [flMarginBottom]="Margin.SMALL"
      >
        <fl-bit class="ContentContainer">
          <fl-input
            class="Input"
            flTrackingLabel="AddTeamMemberInput"
            i18n-placeholder="Username prompt"
            placeholder="Enter a username"
            [flMarginRight]="Margin.SMALL"
            [control]="inputControl"
          >
          </fl-input>
          <fl-spinner
            *ngIf="inputControl.pending"
            class="Icon"
            flTrackingLabel="AddMemberValidationSpinner"
            [flMarginRight]="Margin.SMALL"
            [size]="SpinnerSize.SMALL"
          ></fl-spinner>
          <fl-icon
            *ngIf="inputControl.valid && inputControl.dirty"
            class="Icon"
            name="ui-tick"
            [flMarginRight]="Margin.SMALL"
            [color]="IconColor.SUCCESS"
            [size]="IconSize.MID"
          ></fl-icon>
          <fl-icon
            *ngIf="inputControl.errors && inputControl.dirty"
            class="Icon"
            name="ui-warning-v2"
            [flMarginRight]="Margin.SMALL"
            [color]="IconColor.ERROR"
            [size]="IconSize.MID"
          ></fl-icon>
        </fl-bit>
      </fl-bit>
      <fl-button
        class="AddButton"
        flTrackingLabel="AddTeamMemberButton"
        i18n="Team Payment Setting Modal Add Button"
        [flMarginBottom]="Margin.SMALL"
        [color]="ButtonColor.SECONDARY"
        [size]="ButtonSize.SMALL"
        [disabled]="!inputControl.valid || !inputControl.dirty"
        (click)="handleAdd()"
      >
        Add
      </fl-button>
    </fl-bit>
  `,
  styleUrls: ['./payment-share-add-member-input.component.scss'],
})
export class PaymentShareAddMemberInputComponent implements OnInit, OnDestroy {
  Margin = Margin;
  IconSize = IconSize;
  IconColor = IconColor;
  FontWeight = FontWeight;
  FontColor = FontColor;
  ButtonSize = ButtonSize;
  ButtonColor = ButtonColor;
  SpinnerSize = SpinnerSize;

  @Input() teamMembers$: Rx.Observable<readonly User[]>;
  @Input() validatedUsers$: Rx.Observable<readonly User[]>;
  @Output() userChecked = new EventEmitter<User>();

  inputControl: FormControl;
  private updateValidationSubscription?: Rx.Subscription;

  constructor(private datastore: Datastore, private timeUtils: TimeUtils) {}

  ngOnInit(): void {
    const usernameNotInUseValidator = (errorText: string): AsyncValidatorFn => (
      control: AbstractControl,
    ) => {
      if (!control.valueChanges) {
        return Rx.of(null);
      }

      const username = control.value;
      if (username === undefined || username === '') {
        return Rx.of({});
      }
      const validation$ = this.datastore
        .collection<UsersCollection>('users', query =>
          query.where('username', 'equalsIgnoreCase', username),
        )
        .valueChanges()
        .pipe(
          take(1),
          map(users => {
            if (users.length === 0) {
              return {
                NO_USER_FOUND_ERROR: errorText,
              };
            }
            return null;
          }),
        );
      return this.timeUtils
        .rxTimer(DEBOUNCE_TIME)
        .pipe(switchMap(_ => validation$));
    };

    this.inputControl = new FormControl('', {
      validators: [required($localize`Please provide a username`)],
      asyncValidators: usernameNotInUseValidator(
        `No user was found with this username`,
      ),
    });

    this.updateValidationSubscription = Rx.combineLatest([
      this.teamMembers$,
      this.validatedUsers$,
    ]).subscribe(([teamMembers, validatedUsers]) => {
      this.inputControl.setValidators([
        required(`Please provide a username`),
        notIncludes(
          teamMembers.map(user => user.username),
          `User is already part of the team`,
        ),
        notIncludes(
          validatedUsers.map(user => user.username),
          `User is already part of the team`,
        ),
      ]);
    });
  }

  handleAdd() {
    if (!this.inputControl.valid) {
      dirtyAndValidate(this.inputControl);
      return;
    }

    this.datastore
      .collection<UsersCollection>('users', query =>
        query.where('username', 'equalsIgnoreCase', this.inputControl.value),
      )
      .valueChanges()
      .pipe(take(1))
      .toPromise()
      .then(users => {
        if (users.length === 1) {
          this.inputControl.setValue('');
          this.inputControl.markAsPristine();
          this.inputControl.markAsUntouched();

          this.userChecked.emit(users[0]);
        }
      });
  }

  ngOnDestroy(): void {
    if (this.updateValidationSubscription) {
      this.updateValidationSubscription.unsubscribe();
    }
  }
}
